// Presentation formatters — ported faithfully from the mockup (fmtM / fmtPct / fmtAge /
// fmtTS / fmtBk / hbTitle). These are pure and take already-parsed numbers or epoch-ms.
//
// Rules reproduced from the mockup / design tokens:
//   * Explicit +/- on all P&L when `signed`.
//   * "Unavailable" for undefined values — never NaN, Infinity, or a misleading zero.
//   * Logins are always masked to the last 4 digits (server already sends `login_masked`).

import { toNum } from './decimal';

const CURRENCY_SYMBOL: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', JPY: '¥' };

/** Money in account currency. `v` null/non-finite → "Unavailable". `signed` forces a leading +. */
export function fmtM(v: number | null | undefined, ccy: string, signed = false): string {
  if (v == null || !Number.isFinite(v)) return 'Unavailable';
  const digits = ccy === 'JPY' ? 0 : 2;
  const number = Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  const body = ccy === 'USC' ? number + ' USC' : (CURRENCY_SYMBOL[ccy] ?? ccy + ' ') + number;
  if (v < 0) return '-' + body;
  return signed && v > 0 ? '+' + body : body;
}

/** Convenience: format a decimal STRING as money. */
export function fmtMoney(s: string | null | undefined, ccy: string, signed = false): string {
  return fmtM(toNum(s), ccy, signed);
}

/** Percent. `> 100%` is clamped for readability, exactly like the mockup. */
export function fmtPct(v: number | null | undefined, signed = false): string {
  if (v == null || !Number.isFinite(v)) return 'Unavailable';
  if (v > 100) return '>100%';
  return (signed && v > 0 ? '+' : '') + v.toFixed(1) + '%';
}

export function fmtPctStr(s: string | null | undefined, signed = false): string {
  return fmtPct(toNum(s), signed);
}

/** Relative age from milliseconds — "now", "42s", "3m 5s", "2h 10m", "1d 4h". */
export function fmtAge(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return 'Unavailable';
  if (ms < 1500) return 'now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return s + 's';
  const m = Math.floor(s / 60);
  if (m < 10) return m + 'm ' + (s % 60) + 's';
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ' + (m % 60) + 'm';
  const d = Math.floor(h / 24);
  return d + 'd ' + (h % 24) + 'h';
}

export function pad2(x: number): string {
  return String(x).padStart(2, '0');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Local wall-clock timestamp. Same-day → HH:MM:SS; otherwise → "Mon D HH:MM". */
export function fmtTS(tsMs: number | null | undefined): string {
  if (!tsMs) return '—';
  const d = new Date(tsMs);
  const now = new Date();
  const hm = pad2(d.getHours()) + ':' + pad2(d.getMinutes()) + ':' + pad2(d.getSeconds());
  if (d.toDateString() === now.toDateString()) return hm;
  return MONTHS[d.getMonth()] + ' ' + d.getDate() + ' ' + hm.slice(0, 5);
}

/** UTC wall-clock HH:MM:SS. */
export function fmtUTC(tsMs: number | null | undefined): string {
  if (!tsMs) return '—';
  return new Date(tsMs).toISOString().slice(11, 19);
}

/** Broker-server clock HH:MM for a GMT offset (hours). */
export function fmtBk(tsMs: number | null | undefined, tzOffsetHours: number): string {
  if (!tsMs) return '—';
  const d = new Date(tsMs + tzOffsetHours * 3600000);
  return pad2(d.getUTCHours()) + ':' + pad2(d.getUTCMinutes());
}

/** Broker-server clock HH:MM:SS for a GMT offset (hours). */
export function fmtBkFull(tsMs: number | null | undefined, tzOffsetHours: number): string {
  if (!tsMs) return '—';
  const d = new Date(tsMs + tzOffsetHours * 3600000);
  return pad2(d.getUTCHours()) + ':' + pad2(d.getUTCMinutes()) + ':' + pad2(d.getUTCSeconds());
}

/** Tooltip: "Local HH:MM:SS · broker HH:MM (GMT+N)". */
export function hbTitle(tsMs: number | null | undefined, tzOffsetHours: number): string {
  const sign = tzOffsetHours >= 0 ? '+' : '';
  return `Local ${fmtTS(tsMs)} · broker ${fmtBk(tsMs, tzOffsetHours)} (GMT${sign}${tzOffsetHours})`;
}

export type TzMode = 'both' | 'local' | 'broker';

/** Timestamp respecting the user's tz preference; used where the setting should apply. */
export function fmtStamp(tsMs: number | null | undefined, tzOffsetHours: number, mode: TzMode): string {
  if (!tsMs) return '—';
  const sign = tzOffsetHours >= 0 ? '+' : '';
  switch (mode) {
    case 'local':
      return fmtTS(tsMs);
    case 'broker':
      return `${fmtBkFull(tsMs, tzOffsetHours)} (GMT${sign}${tzOffsetHours})`;
    case 'both':
    default:
      return `${fmtTS(tsMs)} · ${fmtBk(tsMs, tzOffsetHours)} broker`;
  }
}

/** RFC3339 (or null) → epoch ms (or null). */
export function parseTs(s: string | null | undefined): number | null {
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}
